import { useState, useCallback } from 'react';
import { apiFetch } from '../../../config/api';

export const useExperiments = () => {
  const [experiments, setExperiments] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchExperiments = useCallback(async (params = {}) => {
    setLoading(true);
    setError(null);

    try {
      const queryParams = new URLSearchParams();
      if (params.page) queryParams.append('page', params.page);
      if (params.page_size) queryParams.append('page_size', params.page_size);
      if (params.status) queryParams.append('experiment_status', params.status);
      if (params.template_id)
        queryParams.append('template_id', params.template_id);
      if (params.search) queryParams.append('search', params.search);
      if (params.sort_by) queryParams.append('sort_by', params.sort_by);
      if (params.sort_order)
        queryParams.append('sort_order', params.sort_order);

      const queryString = queryParams.toString();
      const endpoint = `/api/v1/experiments/${queryString ? `?${queryString}` : ''}`;

      const data = await apiFetch(endpoint);
      setExperiments(data);
      return data;
    } catch (err) {
      const errorMessage = err.message || 'Failed to fetch experiments';
      setError(errorMessage);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  const createExperiment = useCallback(async experimentData => {
    setLoading(true);
    setError(null);

    try {
      // Creating experiment with data
      const data = await apiFetch('/api/v1/experiments/', {
        method: 'POST',
        body: JSON.stringify(experimentData),
      });
      return data;
    } catch (err) {
      // Experiment creation error
      const errorMessage = err.message || 'Failed to create experiment';
      setError(errorMessage);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  const updateExperiment = useCallback(async (experimentId, experimentData) => {
    setLoading(true);
    setError(null);

    try {
      const data = await apiFetch(`/api/v1/experiments/${experimentId}`, {
        method: 'PUT',
        body: JSON.stringify(experimentData),
      });
      return data;
    } catch (err) {
      const errorMessage = err.message || 'Failed to update experiment';
      setError(errorMessage);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  const deleteExperiment = useCallback(async experimentId => {
    setLoading(true);
    setError(null);

    try {
      const data = await apiFetch(`/api/v1/experiments/${experimentId}`, {
        method: 'DELETE',
      });
      return data;
    } catch (err) {
      const errorMessage = err.message || 'Failed to delete experiment';
      setError(errorMessage);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  const startExperiment = useCallback(async experimentId => {
    setLoading(true);
    setError(null);

    try {
      const data = await apiFetch(`/api/v1/experiments/${experimentId}/start`, {
        method: 'POST',
        body: JSON.stringify({}),
      });
      return data;
    } catch (err) {
      const errorMessage = err.message || 'Failed to start experiment';
      setError(errorMessage);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  const pauseExperiment = useCallback(async experimentId => {
    setLoading(true);
    setError(null);

    try {
      const data = await apiFetch(`/api/v1/experiments/${experimentId}/pause`, {
        method: 'POST',
        body: JSON.stringify({}),
      });
      return data;
    } catch (err) {
      const errorMessage = err.message || 'Failed to pause experiment';
      setError(errorMessage);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  const stopExperiment = useCallback(async experimentId => {
    setLoading(true);
    setError(null);

    try {
      const data = await apiFetch(`/api/v1/experiments/${experimentId}/stop`, {
        method: 'POST',
        body: JSON.stringify({}),
      });
      return data;
    } catch (err) {
      const errorMessage = err.message || 'Failed to stop experiment';
      setError(errorMessage);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  const getExperiment = useCallback(async experimentId => {
    setLoading(true);
    setError(null);

    try {
      const data = await apiFetch(`/api/v1/experiments/${experimentId}`);
      return data;
    } catch (err) {
      const errorMessage = err.message || 'Failed to fetch experiment';
      setError(errorMessage);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  const getExperimentResults = useCallback(async experimentId => {
    setLoading(true);
    setError(null);

    try {
      const data = await apiFetch(
        `/api/v1/experiments/${experimentId}/results`
      );
      return data;
    } catch (err) {
      const errorMessage = err.message || 'Failed to fetch experiment results';
      setError(errorMessage);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  const getTrafficAllocationStrategies = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const data = await apiFetch(
        '/api/v1/experiments/traffic-allocation-strategies'
      );
      return data;
    } catch (err) {
      const errorMessage =
        err.message || 'Failed to fetch traffic allocation strategies';
      setError(errorMessage);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  return {
    experiments,
    loading,
    error,
    fetchExperiments,
    createExperiment,
    updateExperiment,
    deleteExperiment,
    startExperiment,
    pauseExperiment,
    stopExperiment,
    getExperiment,
    getExperimentResults,
    getTrafficAllocationStrategies,
  };
};
